/**
 * The gestures a panel in a public room must not honour.
 *
 * `styles.css` blocks everything CSS can reach — the tap highlight, selection,
 * the callout, scroll chaining, and both kinds of zoom through
 * `touch-action: pan-x pan-y`. Four are left that only script can reach, and
 * each one of them ends in a state a customer cannot get out of: there is no
 * browser chrome to reset from, no keyboard, and no back gesture.
 *
 * It is deliberately *not* a React effect. These are properties of the window
 * rather than of any screen, they must hold while the boot screen is up and
 * during the moment a sheet is unmounting, and an effect that re-registers
 * them on every render is four listeners of drift. `main.tsx` installs them
 * once, before React.
 *
 * The teardown is returned rather than assumed unnecessary, because the tests
 * need the document back.
 */

/**
 * What the last press came from, so the context menu can tell a finger from a
 * mouse.
 *
 * A long press on glass raises the same `contextmenu` a right click does, and
 * on this application it is always an accident: there is nothing on the page
 * to copy, inspect or open in a new tab, and the menu it raises covers the
 * product the customer was reaching for until they tap somewhere else. But
 * suppressing it unconditionally would take right-click away from `pnpm dev`,
 * which is how this tree is inspected — and a kiosk panel never reports
 * `mouse` unless somebody has plugged one in to service it, in which case they
 * want the menu.
 */
type PointerKind = PointerEvent['pointerType']

export function installTouchGuards(target: Document = document): () => void {
  let lastPointer: PointerKind = 'mouse'

  const remember = (event: Event): void => {
    if (event instanceof PointerEvent) lastPointer = event.pointerType
  }

  const contextMenu = (event: Event): void => {
    if (lastPointer !== 'mouse') event.preventDefault()
  }

  /**
   * Nothing on this page is picked up and carried somewhere.
   *
   * The `<img>` rule in `styles.css` stops the common case before it starts;
   * this catches the rest — a name, a price, an icon — because a drag that
   * begins is a tap that never lands, and the customer is left holding a
   * translucent copy of a coffee with nowhere to drop it.
   */
  const dragStart = (event: Event): void => {
    event.preventDefault()
  }

  /**
   * WebKit's pinch, which `touch-action` does not reach.
   *
   * Safari zooms the page through its own non-standard `gesture*` events and
   * honours `touch-action` only for scrolling, so the root's `pan-x pan-y` is
   * the whole answer on Chromium and half of it here. The panel runs WebView2,
   * but every demo of this kiosk is given on an iPad.
   */
  const gesture = (event: Event): void => {
    event.preventDefault()
  }

  /**
   * A trackpad pinch and a `ctrl`-scroll, which are the same event.
   *
   * Not the panel's problem — it is the developer's, and it is the same
   * unrecoverable state: a kiosk zoomed to 150% in a window with no visible
   * chrome looks like a layout bug and gets reported as one.
   */
  const wheel = (event: WheelEvent): void => {
    if (event.ctrlKey) event.preventDefault()
  }

  /**
   * The line that makes `:active` work on WebKit.
   *
   * Every press acknowledgement in this application is a CSS `:active` rule —
   * `.tap`'s 2% scale, the order-type card filling with brand, a payment row
   * taking a yellow border. WebKit applies none of them unless something in
   * the ancestor chain is listening for touch, so on an iPad the entire
   * application presses like a photograph of itself. It is a real empty
   * listener and not a mistake; `passive` because it never calls
   * `preventDefault`.
   */
  const activate = (): void => {}

  target.addEventListener('pointerdown', remember, { capture: true, passive: true })
  target.addEventListener('contextmenu', contextMenu)
  target.addEventListener('dragstart', dragStart)
  target.addEventListener('gesturestart', gesture)
  target.addEventListener('gesturechange', gesture)
  target.addEventListener('wheel', wheel, { passive: false })
  target.addEventListener('touchstart', activate, { passive: true })

  return () => {
    target.removeEventListener('pointerdown', remember, { capture: true })
    target.removeEventListener('contextmenu', contextMenu)
    target.removeEventListener('dragstart', dragStart)
    target.removeEventListener('gesturestart', gesture)
    target.removeEventListener('gesturechange', gesture)
    target.removeEventListener('wheel', wheel)
    target.removeEventListener('touchstart', activate)
  }
}
